import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Typography, Divider, Icon, Tooltip, Button } from 'shared/ui'
import { numberFormatIntUsd, numberFormat } from 'shared/lib'
import {
  PERIOD_PRICE_LABELS,
  PERIOD_PRICE_SHORT_LABELS,
  TOOLTIP_TEXT,
} from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/constants/constants'
import { type CurrentPlanData } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/store/types'
import { type ConvertedCurrentPlanCredits } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/store/opportunityModalStore'
import styles from './styles.module.scss'

type ICurrentPlanCardProps = {
  data: CurrentPlanData
  subscriptionPeriod?: string | null
  showAdditionalPriceInfo: boolean
  convertedCurrentPlanCredits: ConvertedCurrentPlanCredits
  totalCreditsUsed: number
  onKeepCurrentPlan: () => void
  showCustomPlanUI: boolean
  hasTimePeriodTabs: boolean
}

export const CurrentPlanCard = observer(
  ({
    data,
    subscriptionPeriod,
    showAdditionalPriceInfo,
    convertedCurrentPlanCredits,
    totalCreditsUsed,
    onKeepCurrentPlan,
    showCustomPlanUI,
    hasTimePeriodTabs,
  }: ICurrentPlanCardProps) => {
    const periodText = PERIOD_PRICE_LABELS[subscriptionPeriod || ''] || ''
    const periodTextShort = PERIOD_PRICE_SHORT_LABELS[subscriptionPeriod || ''] || ''

    return (
      <div className={styles.planInfo}>
        <Typography ariaLabel='opportunity_modal_current_plan_title' variant='body-lg-semibold'>
          Current plan
        </Typography>
        <div className={styles.currentPaymentCost}>
          <Typography
            ariaLabel='opportunity_modal_payment_cost_title'
            variant='body-md-medium'
            color='var(--red-70)'
          >
            You paid
          </Typography>
        </div>
        <div className={classNames({ [styles.priceRow]: showAdditionalPriceInfo })}>
          <Typography ariaLabel='opportunity_modal_payment_cost_amount' variant='body-xxl-semibold'>
            {numberFormatIntUsd({ value: data.totalCost })}
          </Typography>
          {periodText && (
            <Typography ariaLabel='opportunity_modal_payment_period' variant='body-rg-regular'>
              / {periodText}
            </Typography>
          )}
        </div>
        {showAdditionalPriceInfo && (
          <Typography
            ariaLabel='opportunity_modal_total_credits_used'
            variant='body-md-regular'
            color='var(--content-primary-secondary)'
            className={classNames(styles.totalCreditsUsed)}
          >
            {numberFormat({ value: totalCreditsUsed })} credits / {periodText}
          </Typography>
        )}
        <Typography
          ariaLabel='opportunity_modal_total_credits'
          variant='body-lg-semibold'
          color='var(--content-primary-primary)'
          className={classNames(styles.totalCredits, {
            [styles.withoutTimePeriod]: !hasTimePeriodTabs,
            [styles.showAdditionalPriceInfo]: showAdditionalPriceInfo,
          })}
        >
          {numberFormat({ value: convertedCurrentPlanCredits.totalCredits })} total credits
        </Typography>
        <div className={classNames(styles.dividerWrapper, styles.dividerDefault)}>
          <Divider className={styles.divider} />
        </div>
        <div className={styles.creditsRow}>
          <Typography ariaLabel='opportunity_modal_plan_credits_info' variant='body-lg-regular'>
            {numberFormat({ value: convertedCurrentPlanCredits.planCredits })} plan credits /{' '}
            {showCustomPlanUI ? periodTextShort : convertedCurrentPlanCredits.planPeriod}
          </Typography>
          <Typography ariaLabel='opportunity_modal_plan_credits_price' variant='body-lg-semibold'>
            {numberFormatIntUsd({ value: convertedCurrentPlanCredits.planPrice })}
          </Typography>
        </div>
        <div className={classNames(styles.creditsRow, styles.creditsRowWithMargin)}>
          <Typography ariaLabel='opportunity_modal_overage_credits_info' variant='body-lg-regular'>
            {numberFormat({ value: convertedCurrentPlanCredits.overageCredits })} overage credits
            <Tooltip label={TOOLTIP_TEXT} placement='top'>
              <Icon
                icon='question'
                fontSize={14}
                color='var(--content-neutral-primary)'
                className={styles.overageCreditsIcon}
              />
            </Tooltip>
          </Typography>
          <Typography ariaLabel='opportunity_modal_overage_credits_cost' variant='body-lg-semibold'>
            {numberFormatIntUsd({ value: convertedCurrentPlanCredits.overageCost })}
          </Typography>
        </div>
        <Typography
          ariaLabel='opportunity_modal_total_cost'
          variant='body-md-regular'
          color='var(--content-primary-tertiary)'
        >
          {numberFormat({
            value: data.additionalCreditPrice,
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          })}
          &nbsp; per additional credit
        </Typography>

        <Button
          text='Keep my current plan'
          typeBtn='contained'
          contained='tertiary'
          size='large'
          className={classNames(styles.keepCurrentPlanButton, {
            [styles.isButtonPressDown]: showCustomPlanUI,
          })}
          fullWidth
          onClick={onKeepCurrentPlan}
        />
      </div>
    )
  }
)
